import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
import { toast } from 'react-toastify';
import MssDataGridPro from '../../common/MssDataGridPro';
import { useIsNarrowScreen } from '../../common/useIsNarrowScreen';
import MssAlert from '../../common/MssAlert';
import MssApiError from '../../common/MssApiError';
import { SearchBar } from '../../Dashboard/Desktop/ResourcesTable/SearchBar';
import { getColumns } from './columns';
import FeedbackDetails from './FeedbackDetails';
import { getData, patchData } from '../../../DataAccessLayer';
import { feedback as feedbackService, feedbackItem } from '../../../DataAccessLayer/services';

const PAGE_ID = 'adminFeedback';

/** Everything a reviewer might search by, as one lowercase haystack. */
const searchableText = (item) =>
  [
    item.userId,
    item.courseId,
    item.pageUrl,
    item.bugDetail,
    item.ideaDetail,
    item.changeDetail,
    item.otherDetail,
    item.note,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

/**
 * What learners have told us, for the people who can do something about it.
 *
 * A developer-portal page only: the route it reads is registered under /dev,
 * which every request reaches through a verified identity and an approved
 * entitlement, so this is never served to a Canvas launch.
 */
function AdminFeedback() {
  const isNarrow = useIsNarrowScreen();
  const [feedback, setFeedback] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectionCount, setSelectionCount] = useState(0);
  const detailsRef = useRef(null);

  const loadFeedback = async () => {
    setIsLoading(true);
    setLoadError(false);
    try {
      setFeedback(await getData(feedbackService));
    } catch (err) {
      console.log(err);
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFeedback();
  }, []);

  const visibleFeedback = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return feedback;
    return feedback.filter((item) => searchableText(item).includes(term));
  }, [feedback, searchTerm]);

  // Read from the list rather than held separately, so a row that has just been
  // reviewed or annotated shows its new state without a second fetch.
  const selectedFeedback = feedback.find((item) => item.id === selectedId) ?? null;

  const handleSelect = (row) => {
    setSelectedId(row.id);
    // Counted rather than watched: a second tap on the row already open is
    // still a tap, and on a phone it should still go to the submission.
    setSelectionCount((count) => count + 1);
  };

  /*
   * Side by side on a wide screen, the submission is already in view. On a
   * phone it is under the list, often a screen away, so the tap that opens one
   * also takes the reviewer to it.
   *
   * Here rather than in the handler so the panel has rendered what was picked:
   * while it still holds the empty message the page is not long enough to
   * scroll that far, and the scroll would be clipped to a document that is
   * about to grow.
   */
  useEffect(() => {
    if (!isNarrow || !selectionCount) return;
    detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [selectionCount, isNarrow]);

  /** Writes one reviewer's change into the row it belongs to. */
  const applyUpdate = (feedbackId, updates) =>
    setFeedback((current) =>
      current.map((item) => (item.id === feedbackId ? { ...item, ...updates } : item)),
    );

  const handleToggleReviewed = async (row) => {
    const isReviewed = !row.isReviewed;
    // Turned over on screen first: the reviewer is going down a list, and a
    // round trip between each click would make the list fight back.
    applyUpdate(row.id, { isReviewed });
    try {
      await patchData(feedbackItem(row.id), { isReviewed });
    } catch (err) {
      console.log(err);
      applyUpdate(row.id, { isReviewed: row.isReviewed });
      toast.error('Failed to update feedback. Please try again.');
    }
  };

  const handleSaveNote = async (note) => {
    setIsSaving(true);
    try {
      await patchData(feedbackItem(selectedId), { note });
      applyUpdate(selectedId, { note });
      toast.success('Note saved.');
    } catch (err) {
      console.log(err);
      toast.error('Failed to save the note. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box className="mss__adminFeedback" id={PAGE_ID}>
      <Box className="resourceHeader" id={`${PAGE_ID}__box_header`}>
        <Box className="resourceTitleGroup">
          <Box className="resourceTitle">
            <Typography variant="h2" component="h1">
              Feedback
            </Typography>
          </Box>
          <Typography className="resourceCount">
            <strong>{feedback.length}</strong> submitted
          </Typography>
        </Box>
      </Box>

      {loadError ? (
        <MssAlert id={`${PAGE_ID}__alert_loadError`} severity="error" type="statusAlert">
          <MssApiError id={PAGE_ID} widgetName="Feedback" isPage reload={loadFeedback} />
        </MssAlert>
      ) : (
        <Box className="feedbackLayout">
          <Box className="mss__box feedbackTable" id={`${PAGE_ID}__box_table`}>
            {/*
              Searched the way each screen's dashboard searches: a phone gets
              the field itself, always open and full width with the magnifier
              inside it, because there is no second table here to make room
              for and nothing else competing for the row.
            */}
            {isNarrow ? (
              <TextField
                id={`${PAGE_ID}__textField_search`}
                className="mobileSearch"
                size="small"
                fullWidth
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search feedback"
                slotProps={{
                  input: {
                    endAdornment: (
                      <FontAwesomeIcon icon={faMagnifyingGlass} className="searchIcon" />
                    ),
                  },
                  htmlInput: { 'aria-label': 'Search feedback' },
                }}
              />
            ) : (
              <SearchBar
                id={PAGE_ID}
                label="feedback"
                value={searchTerm}
                onChange={setSearchTerm}
              />
            )}
            {/*
              One table at every width: the submissions are many and only grow,
              and the pager is how a reviewer gets through them. A phone is
              given the four columns that fit it (see getColumns); the two it
              drops are in the submission the row opens underneath.
            */}
            <MssDataGridPro
              id={`${PAGE_ID}__grid`}
              className="mss__dataGrid"
              rows={visibleFeedback}
              columns={getColumns({
                id: PAGE_ID,
                onToggleReviewed: handleToggleReviewed,
                isNarrow,
              })}
              isLoading={isLoading}
              onRowClick={({ row }) => handleSelect(row)}
              // Marks the open row, so it is clear which submission is below.
              getRowClassName={({ id: rowId }) => (rowId === selectedId ? 'selectedRow' : '')}
              /*
               * No per-column menu on a phone: its button is drawn over the
               * narrowest headers and hangs past the last of them, which is
               * all the sideways scroll the four columns would otherwise have.
               */
              disableColumnMenu={isNarrow}
              initialState={{
                pagination: { paginationModel: { pageSize: 20 } },
                sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] },
              }}
              slots={{
                noRowsOverlay: () => (
                  <Box className="noRowsMessage">
                    {searchTerm.trim() ? 'No feedback matches your search.' : 'No feedback yet.'}
                  </Box>
                ),
              }}
              pagination
            />
          </Box>

          <FeedbackDetails
            ref={detailsRef}
            id={PAGE_ID}
            feedback={selectedFeedback}
            isSaving={isSaving}
            onSaveNote={handleSaveNote}
          />
        </Box>
      )}
    </Box>
  );
}

export default AdminFeedback;
